"use client";
import React, { useState, useEffect } from "react";
import Spinner from "react-bootstrap/Spinner";
import {
  loginHandler,
  submitMfaCode,
  associateMfa,
  confirmMfaSetup,
} from "../services/api/authHandler";
import { QRCodeSVG } from "qrcode.react";
import { orgChecker } from "../services/api/orgChecker";
import { isSuperAdminSubdomain } from "../services/utils/utiltyFunctions";
import { checkUserInOrg } from "../services/api/checkUserInOrg";
import {
  requestPasswordReset,
  confirmPasswordReset,
} from "../services/api/forgotPasswordHandler";
import { useAuth } from "../components/Contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  BsShieldCheck,
  BsEnvelope,
  BsLock,
  BsKey,
  BsEye,
  BsEyeSlash,
  BsExclamationCircle,
  BsArrowLeft,
  BsCheckCircle,
} from "react-icons/bs";
import "./Login.css";

interface OrgResponse {
  message: string;
  friendlyname: string;
}

const emailRegex = /^[a-zA-Z0-9]+@[a-zA-Z0-9]+\.[A-Za-z]+$/;

export default function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [isOrgLoading, setOrgLoading] = useState(true);
  const [orgFound, setOrgFound] = useState<OrgResponse | null>(null);

  // Login + flow modes share the same card layout to keep the user on one
  // screen. "signin" = email/password. "request"/"confirm" = forgot-password.
  // "mfa-totp" = user has an authenticator app set up, enter a code.
  // "mfa-setup" = first time after MFA was required, scan QR + verify.
  const [mode, setMode] = useState<
    "signin" | "request" | "confirm" | "mfa-totp" | "mfa-setup"
  >("signin");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetInfoMsg, setResetInfoMsg] = useState("");

  // MFA state shared by both TOTP modes. mfaSession is the Cognito challenge
  // handle — rotated by each backend call, must be passed back on the next.
  // mfaCode is the 6-digit code input. mfaOtpauthUrl is the otpauth://
  // string we render as a QR code in setup mode. mfaSecret is the raw
  // base32 secret in case the user wants to type it instead of scanning.
  const [mfaSession, setMfaSession] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaOtpauthUrl, setMfaOtpauthUrl] = useState("");
  const [mfaSecret, setMfaSecret] = useState("");

  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isAuthenticated) navigate("/dashboard");
  }, [auth.isAuthenticated, navigate]);

  useEffect(() => {
    // The admin subdomain is the platform-owner shell, not a real org.
    // Skip org-checker and let the standard login flow proceed; auth
    // role is what gates SuperAdminDashboard, not the subdomain.
    if (isSuperAdminSubdomain()) {
      setOrgFound({ message: "FOUND_ORG", friendlyname: "EpicareAlert Platform" });
      setOrgLoading(false);
      return;
    }
    orgChecker()
      .then(setOrgFound)
      .catch(() => setOrgFound(null))
      .finally(() => setOrgLoading(false));
  }, []);

  // After Cognito + MFA succeed, resolve the user record and finish login.
  // On admin.epicarealert.com we use /api/v1/verify-session (looks up by
  // sub, no org constraint) since the platform-owner doesn't belong to any
  // specific org's user list. On org subdomains we use the existing
  // checkUserInOrg flow.
  const finalizeLogin = async (sub: string) => {
    if (isSuperAdminSubdomain()) {
      const r = await fetch("/api/v1/verify-session", { credentials: "include" });
      if (!r.ok) throw new Error("Session verification failed.");
      const data = await r.json();
      if (!data?.isAuthenticated || !data?.user) {
        throw new Error("User account not found.");
      }
      if (data.user.role !== "superadmin") {
        throw new Error("This account does not have platform-admin access.");
      }
      auth.login(data.user);
      return;
    }
    const userExists = await checkUserInOrg(sub);
    if (!userExists) throw new Error("User does not exist in the organization.");
    auth.login(userExists.user);
  };

  const switchTo = (next: "signin" | "request" | "confirm") => {
    setErrorMsg("");
    setResetInfoMsg("");
    setMode(next);
  };

  const handleRequestReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg("");
    setResetInfoMsg("");
    if (!emailRegex.test(email)) {
      setErrorMsg("Email address is invalid.");
      return;
    }
    setLoading(true);
    try {
      const result = await requestPasswordReset(email);
      if (result?.message === "Success") {
        setResetInfoMsg(
          "If that email is registered, a verification code has been sent. Check your inbox and spam folder.",
        );
        setMode("confirm");
      } else {
        setErrorMsg(result?.message || "Could not start password reset.");
      }
    } catch (err: any) {
      setErrorMsg(err.message || "An unknown error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg("");
    setResetInfoMsg("");
    if (!resetCode.trim()) {
      setErrorMsg("Enter the verification code from your email.");
      return;
    }
    if (newPassword.length < 8) {
      setErrorMsg("New password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const result = await confirmPasswordReset(email, resetCode.trim(), newPassword);
      if (result?.message === "Success") {
        // Bounce back to signin with the email pre-filled so they can sign in
        // with the new password immediately. Clear the reset fields.
        setResetCode("");
        setNewPassword("");
        setShowNewPassword(false);
        setMode("signin");
        setResetInfoMsg("Password reset. Sign in with your new password.");
      } else {
        setErrorMsg(result?.message || "Could not reset password.");
      }
    } catch (err: any) {
      setErrorMsg(err.message || "An unknown error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg("");
    setResetInfoMsg("");
    if (!emailRegex.test(email)) {
      setErrorMsg("Email address is invalid.");
      return;
    }
    setLoading(true);
    try {
      const login = await loginHandler(email, password);

      // MFA branch — backend says password accepted, now we either need
      // the code from the user's authenticator app (TOTP) or to set one up
      // for the first time (MFA_SETUP).
      if (login?.mfaRequired) {
        setMfaSession(login.session || "");
        setMfaCode("");

        if (login.challengeName === "MFA_SETUP") {
          // First time — call associate to get the QR-code URL.
          const assoc = await associateMfa(email, login.session || "");
          if (!assoc.ok) throw new Error(assoc.message || "Failed to start MFA setup.");
          setMfaOtpauthUrl(assoc.otpauthUrl || "");
          setMfaSecret(assoc.secret || "");
          setMfaSession(assoc.session || login.session || "");
          setMode("mfa-setup");
        } else {
          // SOFTWARE_TOKEN_MFA — they're already set up.
          setMode("mfa-totp");
        }
        return;
      }

      if (login?.message !== "Success") throw new Error(login.message);
      if (!login?.sub) throw new Error("User account not found.");
      await finalizeLogin(login.sub);
    } catch (error: any) {
      setErrorMsg(error.message || "An unknown error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // Subsequent-login: existing TOTP user enters code from their app.
  const handleVerifyMfa = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg("");
    if (!mfaCode.trim() || mfaCode.trim().length < 6) {
      setErrorMsg("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setLoading(true);
    try {
      const result = await submitMfaCode(email, mfaSession, mfaCode.trim());
      if (!result.ok) {
        throw new Error(result.message || "Code verification failed.");
      }
      if (!result.sub) throw new Error("User account not found.");
      await finalizeLogin(result.sub);
    } catch (error: any) {
      setErrorMsg(error.message || "An unknown error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // First-time setup: user has scanned the QR, types their first code, we
  // verify and complete the login. Cognito flips their MFA preference to
  // SOFTWARE_TOKEN_MFA enabled on success, so future logins skip this mode.
  const handleConfirmMfaSetup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg("");
    if (!mfaCode.trim() || mfaCode.trim().length < 6) {
      setErrorMsg("Enter the 6-digit code your app shows.");
      return;
    }
    setLoading(true);
    try {
      const result = await confirmMfaSetup(email, mfaSession, mfaCode.trim());
      if (!result.ok) {
        throw new Error(result.message || "Setup verification failed.");
      }
      if (!result.sub) throw new Error("User account not found.");
      await finalizeLogin(result.sub);
    } catch (error: any) {
      setErrorMsg(error.message || "An unknown error occurred.");
    } finally {
      setLoading(false);
    }
  };

  if (auth.isLoading && auth.isAuthenticated) {
    return <Spinner animation="border" style={{ margin: "20px auto", display: "block" }} />;
  }

  if (isOrgLoading) {
    return <Spinner animation="border" style={{ margin: "20px auto", display: "block" }} />;
  }

  if (!orgFound) {
    return (
      <div className="login-page">
        <div className="login-center">
          <div className="login-card" style={{ textAlign: "center" }}>
            <div className="login-card-title" style={{ marginBottom: 8 }}>Organization not found</div>
            <p className="login-card-sub">The URL you are using does not belong to a registered organization.</p>
          </div>
        </div>
      </div>
    );
  }

  // Platform-owner login (admin.epicarealert.com) gets a darker, indigo-accented
  // variant so it's visually obvious this isn't a regular hospital tenant login.
  // Same layout / form / flow — just chrome differences (background, badge,
  // copy). Driven by the `platform` modifier class on .login-page.
  const isPlatform = isSuperAdminSubdomain();
  const accent = isPlatform ? "#6366f1" : "#2563eb";

  return (
    <div className={isPlatform ? "login-page platform" : "login-page"}>
      {/* ECG background */}
      <svg
        className="login-ecg"
        viewBox="0 0 1440 80"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <polyline
          points="0,40 320,40 360,40 375,15 385,65 395,40 410,40 730,40 750,40 760,18 768,62 775,40 790,40 1440,40"
          fill="none"
          stroke="#94a3b8"
          strokeWidth="1.5"
        />
      </svg>

      {/* Top bar */}
      <div className="login-topbar">
        <div className="login-logo">
          <div className="login-logo-icon">
            <BsShieldCheck size={15} color={accent} />
          </div>
          {isPlatform ? "EpiCareAlert Platform" : "EpiCareAlert"}
        </div>
        <div className="login-status">
          <span className="login-status-dot" />
          {isPlatform ? "Platform Admin" : "All systems operational"}
        </div>
      </div>

      {/* Centered card */}
      <div className="login-center">
        <div className="login-card">
          <div className="login-card-header">
            <div className="login-card-icon">
              <BsShieldCheck size={24} color={accent} />
            </div>
            <h1 className="login-card-title">
              {mode === "signin" && (isPlatform ? "Sign in to the Platform" : "Sign in to EpiCareAlert")}
              {mode === "request" && "Reset your password"}
              {mode === "confirm" && "Enter verification code"}
              {mode === "mfa-totp" && "Verify it's you"}
              {mode === "mfa-setup" && "Set up two-factor authentication"}
            </h1>
            <p className="login-card-sub">
              {mode === "signin" && (isPlatform ? "Restricted access — platform staff only" : "Access the healthcare management system")}
              {mode === "request" && "We'll email you a verification code"}
              {mode === "confirm" && "Then choose a new password"}
              {mode === "mfa-totp" && "Enter the 6-digit code from your authenticator app"}
              {mode === "mfa-setup" && "One-time setup — required to continue"}
            </p>
          </div>

          {errorMsg && (
            <div className="login-error">
              <BsExclamationCircle size={14} />
              {errorMsg}
            </div>
          )}

          {resetInfoMsg && (
            <div
              className="login-error"
              style={{
                background: "rgba(34,197,94,0.08)",
                borderColor: "rgba(34,197,94,0.4)",
                color: "#166534",
              }}
            >
              <BsCheckCircle size={14} />
              {resetInfoMsg}
            </div>
          )}

          {mode === "signin" && (
            <form onSubmit={handleLogin}>
              <div className="login-field">
                <label className="login-label">Email address</label>
                <div className="login-input-wrap">
                  <BsEnvelope className="login-input-icon" />
                  <input
                    className="login-input"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-label">Password</label>
                <div className="login-input-wrap">
                  <BsLock className="login-input-icon" />
                  <input
                    className="login-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="current-password"
                    style={{ paddingRight: 38 }}
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                  >
                    {showPassword ? <BsEyeSlash /> : <BsEye />}
                  </button>
                </div>
              </div>

              <div className="login-row">
                <label className="login-remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  Remember me
                </label>
                <button
                  type="button"
                  className="login-forgot"
                  onClick={() => switchTo("request")}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                >
                  Forgot password?
                </button>
              </div>

              <button className="login-btn" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <BsLock size={14} />
                    Sign in
                  </>
                )}
              </button>

              <div className="login-secure">
                <BsShieldCheck size={12} />
                Secure system access
              </div>
            </form>
          )}

          {mode === "request" && (
            <form onSubmit={handleRequestReset}>
              <div className="login-field">
                <label className="login-label">Email address</label>
                <div className="login-input-wrap">
                  <BsEnvelope className="login-input-icon" />
                  <input
                    className="login-input"
                    type="email"
                    placeholder="Enter your account email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              <button className="login-btn" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    Sending code...
                  </>
                ) : (
                  <>
                    <BsEnvelope size={14} />
                    Send verification code
                  </>
                )}
              </button>

              <button
                type="button"
                className="login-forgot"
                onClick={() => switchTo("signin")}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  marginTop: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <BsArrowLeft size={12} />
                Back to sign in
              </button>
            </form>
          )}

          {mode === "confirm" && (
            <form onSubmit={handleConfirmReset}>
              <div className="login-field">
                <label className="login-label">Email address</label>
                <div className="login-input-wrap">
                  <BsEnvelope className="login-input-icon" />
                  <input
                    className="login-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-label">Verification code</label>
                <div className="login-input-wrap">
                  <BsKey className="login-input-icon" />
                  <input
                    className="login-input"
                    type="text"
                    placeholder="6-digit code from email"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    disabled={isLoading}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-label">New password</label>
                <div className="login-input-wrap">
                  <BsLock className="login-input-icon" />
                  <input
                    className="login-input"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="At least 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="new-password"
                    style={{ paddingRight: 38 }}
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowNewPassword((v) => !v)}
                    tabIndex={-1}
                  >
                    {showNewPassword ? <BsEyeSlash /> : <BsEye />}
                  </button>
                </div>
              </div>

              <button className="login-btn" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <BsCheckCircle size={14} />
                    Reset password
                  </>
                )}
              </button>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 14,
                  gap: 12,
                }}
              >
                <button
                  type="button"
                  className="login-forgot"
                  onClick={() => switchTo("signin")}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <BsArrowLeft size={12} />
                  Back to sign in
                </button>
                <button
                  type="button"
                  className="login-forgot"
                  onClick={() => switchTo("request")}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  Resend code
                </button>
              </div>
            </form>
          )}

          {mode === "mfa-totp" && (
            <form onSubmit={handleVerifyMfa}>
              <div className="login-field">
                <label className="login-label">Authenticator code</label>
                <div className="login-input-wrap">
                  <BsKey className="login-input-icon" />
                  <input
                    className="login-input"
                    type="text"
                    placeholder="6-digit code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    disabled={isLoading}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </div>
              </div>

              <button className="login-btn" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <BsCheckCircle size={14} />
                    Verify
                  </>
                )}
              </button>

              <button
                type="button"
                className="login-forgot"
                onClick={() => {
                  setMode("signin");
                  setMfaCode("");
                  setMfaSession("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  marginTop: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <BsArrowLeft size={12} />
                Back to sign in
              </button>
            </form>
          )}

          {mode === "mfa-setup" && (
            <form onSubmit={handleConfirmMfaSetup}>
              <div style={{ fontSize: "13px", color: "#374151", marginBottom: "14px", lineHeight: 1.55 }}>
                Install an authenticator app on your phone
                (we recommend <strong>Microsoft Authenticator</strong>), scan the
                QR code, then enter the 6-digit code it displays.
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "18px",
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  marginBottom: "12px",
                }}
              >
                {mfaOtpauthUrl ? (
                  <QRCodeSVG value={mfaOtpauthUrl} size={184} includeMargin />
                ) : (
                  <Spinner animation="border" />
                )}
              </div>

              {mfaSecret && (
                <div style={{ fontSize: "11px", color: "#6b7280", textAlign: "center", marginBottom: "14px", wordBreak: "break-all" }}>
                  Can't scan? Enter this manually:<br />
                  <code style={{ fontSize: "12px", color: "#374151", letterSpacing: "0.05em" }}>{mfaSecret}</code>
                </div>
              )}

              <div className="login-field">
                <label className="login-label">Enter code from your app</label>
                <div className="login-input-wrap">
                  <BsKey className="login-input-icon" />
                  <input
                    className="login-input"
                    type="text"
                    placeholder="6-digit code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    disabled={isLoading}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </div>
              </div>

              <button className="login-btn" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <BsCheckCircle size={14} />
                    Confirm and sign in
                  </>
                )}
              </button>

              <button
                type="button"
                className="login-forgot"
                onClick={() => {
                  setMode("signin");
                  setMfaCode("");
                  setMfaSession("");
                  setMfaOtpauthUrl("");
                  setMfaSecret("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  marginTop: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <BsArrowLeft size={12} />
                Cancel
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="login-footer">
        &copy; {new Date().getFullYear()} {orgFound.friendlyname}. All rights reserved.
      </div>
    </div>
  );
}
